import { useEffect, useState } from 'react';
import { useBranch } from '@/context/BranchContext';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import { PrintService } from '@/services/print/PrintService';
import { buildTestPrintHTML } from '@/services/print/invoiceTemplate';
import type { Printer, PrinterRole } from '@/services/print/printTypes';
import { PRINTER_ROLES, ROLE_META, usePrinterSettings } from '@/hooks/usePrinterSettings';
import PrinterConfigCard from './components/PrinterConfigCard';

export default function PrintersSettingsPage() {
  const { branches, activeBranchId, setActiveBranchId } = useBranch();
  const runtime = PrintService.runtime;
  const { settings, loading, saving, error, update, save } = usePrinterSettings(activeBranchId);

  const [printers, setPrinters] = useState<Printer[]>([]);
  const [listingPrinters, setListingPrinters] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  useEffect(() => {
    if (runtime !== 'ELECTRON') return;
    let active = true;
    setListingPrinters(true);
    PrintService.listPrinters()
      .then((list) => {
        if (active) setPrinters(list);
      })
      .finally(() => {
        if (active) setListingPrinters(false);
      });
    return () => {
      active = false;
    };
  }, [runtime]);

  const handleTest = async (role: PrinterRole) => {
    const s = settings?.[role];
    if (!s) return;
    setTestMessage(null);
    const html = buildTestPrintHTML({
      printerName: s.printer_name,
      paperWidth: s.paper_width,
      runtime,
    });
    const result = await PrintService.printHTML(html, {
      paperWidth: s.paper_width,
      printerName: s.printer_name ?? undefined,
    });
    setTestMessage(
      result.success
        ? 'Prueba enviada correctamente.'
        : result.message || 'No se pudo imprimir la prueba.',
    );
  };

  const handleSave = async () => {
    const ok = await save();
    setSuccess(ok ? 'Configuración de impresoras guardada correctamente.' : null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <SettingsBreadcrumb label="Impresoras" icon="ri-printer-line" />
      <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Impresoras
      </h1>
      <p className="mt-2 text-foreground-600 max-w-2xl">
        Configura las impresoras de facturas, reportes y etiquetas. La configuración se guarda por
        sucursal y se usa automáticamente en el punto de venta.
      </p>

      {/* Indicador de entorno */}
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-accent-500" />
          <h2 className="font-heading font-semibold text-foreground-950">
            {runtime === 'ELECTRON' ? 'Modo Electron' : 'Modo Web'}
          </h2>
        </div>
        <p className="mt-2 text-sm text-foreground-600">
          {runtime === 'ELECTRON'
            ? 'Aplicación de escritorio Electron. Impresión directa, detección de impresoras, corte de papel y gaveta cuando el hardware lo permita.'
            : 'Aplicación ejecutándose en navegador. Las impresiones utilizan el sistema de impresión del dispositivo.'}
        </p>

        {runtime === 'ELECTRON' && (
          <div className="mt-4">
            <p className="text-sm font-medium text-foreground-800 mb-2">Impresoras disponibles</p>
            {listingPrinters ? (
              <div className="w-6 h-6 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            ) : printers.length === 0 ? (
              <p className="text-sm text-foreground-500">
                No se detectaron impresoras en este equipo. Conecta una impresora y recarga.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-foreground-600 border-b border-background-200">
                  <tr>
                    <th className="py-2 font-medium">Nombre</th>
                    <th className="py-2 font-medium">Estado</th>
                    <th className="py-2 font-medium">Tipo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {printers.map((p) => (
                    <tr key={p.id}>
                      <td className="py-2 text-foreground-900">{p.name}</td>
                      <td className="py-2 text-foreground-700">{p.status}</td>
                      <td className="py-2 text-foreground-700">
                        {p.type === 'thermal' ? 'Térmica' : 'A4'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Selector de sucursal */}
      {branches.length > 1 && (
        <div className="mt-6">
          <label className="block text-sm font-medium text-foreground-800 mb-1.5">Sucursal</label>
          <select
            value={activeBranchId ?? ''}
            onChange={(e) => setActiveBranchId(e.target.value || null)}
            className="w-full sm:w-80 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tarjetas de configuración */}
      <div className="mt-6 space-y-5">
        {loading || !settings ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          PRINTER_ROLES.map((role) => (
            <PrinterConfigCard
              key={role}
              role={role}
              title={ROLE_META[role].title}
              icon={ROLE_META[role].icon}
              settings={settings[role]}
              runtime={runtime}
              printers={printers}
              disabled={role === 'label'}
              onChange={(patch) => update(role, patch)}
              onTest={() => handleTest(role)}
            />
          ))
        )}
      </div>

      {/* Mensajes */}
      {testMessage && (
        <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700">
          {testMessage}
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {success}
        </div>
      )}

      {/* Guardar */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !settings}
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
        >
          <i className="ri-save-3-line" />
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}